import type { Block } from '@w/content-blocks'
import type { BlockEditorReviewConfig, BlockReviewSelection, BlockReviewSelectionSegment } from '../../types/review.types'
import { blockElement } from './context'

/** Use the first selected line above a selection, or all lines when placing below. */
export function getOwnedReviewSelectionRect(root: HTMLElement, whole = false): DOMRect | null {
  const selection = window.getSelection()
  if (!selection || !selection.rangeCount || selection.isCollapsed ||
    !selection.anchorNode || !selection.focusNode ||
    !root.contains(selection.anchorNode) || !root.contains(selection.focusNode)) return null
  const range = selection.getRangeAt(0)
  return (whole ? null : range.getClientRects()[0]) ?? range.getBoundingClientRect()
}

export function reviewToolbarPosition(
  root: HTMLElement,
  rect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>,
  width: number,
  height: number,
  // Touch browsers draw their own callout above a selection; ours goes below it.
  preferBelow = false,
) {
  let topEdge = 0
  let bottomEdge = window.innerHeight
  let leftEdge = 0
  let rightEdge = window.innerWidth
  for (let parent = root.parentElement; parent; parent = parent.parentElement) {
    if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(parent).overflowY)) continue
    const viewport = parent.getBoundingClientRect()
    topEdge = Math.max(topEdge, viewport.top)
    bottomEdge = Math.min(bottomEdge, viewport.bottom)
    leftEdge = Math.max(leftEdge, viewport.left)
    rightEdge = Math.min(rightEdge, viewport.right)
  }
  const above = rect.top - height - 8
  const below = rect.bottom + 8
  const fitsAbove = above >= topEdge + 8
  const fitsBelow = below + height <= bottomEdge - 8
  const wanted = preferBelow ? (fitsBelow ? below : above) : (fitsAbove ? above : below)
  const top = Math.max(topEdge + 8, Math.min(wanted, bottomEdge - height - 8))
  const left = Math.max(leftEdge + 8, Math.min(rect.left + rect.width / 2 - width / 2, rightEdge - width - 8))
  return { top, left }
}

export function sameReviewSelection(
  left: BlockReviewSelection | null,
  right: BlockReviewSelection | null,
) {
  if (left === right) return true
  if (!left || !right) return false
  if (
    left.scope !== right.scope ||
    left.text !== right.text ||
    left.segments.length !== right.segments.length
  ) {
    return false
  }
  return left.segments.every((segment, index) => {
    const other = right.segments[index]
    return (
      other?.diffId === segment.diffId &&
      other?.diffKind === segment.diffKind &&
      other?.blockId === segment.blockId &&
      other.side === segment.side &&
      other.range.startLine === segment.range.startLine &&
      other.range.endLine === segment.range.endLine
    )
  })
}


export function sourceForPath(path: readonly Block[], config: BlockEditorReviewConfig): BlockReviewSelectionSegment | null {
  const nearest = path.toReversed()
  const source = nearest.find(block => config.sourceMap.has(block.id))
  if (!source) return null
  const range = config.sourceMap.get(source.id)
  if (!range) return null
  const diff = nearest.find(block => block.type === 'diff')
  const sideBlock = nearest.find(block => config.sourceSides?.has(block.id))
  const side = diff?.type === 'diff' && diff.kind !== 'context' ? diff.kind === 'delete' ? 'old' : 'new' : sideBlock ? config.sourceSides?.get(sideBlock.id) : undefined
  return { blockId: source.id, range, ...(diff?.type === 'diff' ? { diffId: diff.id, diffKind: diff.kind } : {}), ...(side ? { side } : {}) }
}

export function blockPaths(blocks: readonly Block[]): Map<string, Block[]> {
  const result = new Map<string, Block[]>()
  function visit(block: Block, parents: Block[]) {
    const path = [...parents, block]
    result.set(block.id, path)
    if ('children' in block) for (const child of block.children ?? []) visit(child, path)
  }
  for (const block of blocks) visit(block, [])
  return result
}

export function readBlockReviewTarget(root: HTMLElement | null, blocks: readonly Block[], blockId: string, config: BlockEditorReviewConfig): BlockReviewSelection | null {
  const path = blockPaths(blocks).get(blockId) ?? []
  const block = path.at(-1)
  const element = blockElement(root, blockId) ?? (block?.type === 'review' && block.children[0] ? blockElement(root, block.children[0].id) : null)
  const segment = sourceForPath(path, config)
  if (!element || !segment) return null
  const { bottom, left, top, width } = element.getBoundingClientRect()
  return { ...(block?.type === 'diff' ? {} : { scope: 'block' as const }), text: element.textContent ?? '', segments: [segment], rect: { bottom, left, top, width } }
}

export function readReviewSelection(root: HTMLElement, blocks: readonly Block[], config: BlockEditorReviewConfig): BlockReviewSelection | null {
  const rect = getOwnedReviewSelectionRect(root)
  const selection = root.ownerDocument.getSelection()
  if (!rect || !selection?.rangeCount) return null
  const range = selection.getRangeAt(0)
  const paths = blockPaths(blocks)
  const segments = new Map<string, BlockReviewSelectionSegment>()
  for (const { node } of selectedTexts(root, range)) {
    const element = node.parentElement?.closest<HTMLElement>('[data-block-id]')
    const source = sourceForPath(paths.get(element?.dataset.blockId ?? '') ?? [], config)
    if (source) segments.set(`${source.blockId}:${source.diffId ?? ''}:${source.side ?? ''}`, source)
  }
  if (segments.size === 0) return null
  return { text: selection.toString(), segments: [...segments.values()].toSorted((a, b) => a.range.startLine - b.range.startLine || a.range.endLine - b.range.endLine),
    rect: { bottom: rect.bottom, left: rect.left, top: rect.top, width: rect.width } }
}

export function selectedTexts(root: HTMLElement, range: Range): { node: Text; start: number; end: number }[] {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const result: { node: Text; start: number; end: number }[] = []
  let next: Node | null
  while ((next = walker.nextNode())) {
    if (!range.intersectsNode(next)) continue
    const node = next as Text
    const start = node === range.startContainer ? range.startOffset : 0
    const end = node === range.endContainer ? range.endOffset : node.length
    if (end > start) result.push({ node, start, end })
  }
  return result
}
